// client/routing-state-signal.ts
import { computed, Signal } from "@lit-labs/preact-signals";
import { routerConfig } from "../config.js";
import { NO_CHANGE as NO_CHANGE_X } from "../no-change.js";
var NO_CHANGE = NO_CHANGE_X;
var cleanPath = (path) => {
  path = decodeURI(path);
  return path.slice(-1)[0] === "/" && path.length > 1 ? path.slice(0, -1) : path;
};
var cleanSearchParams = (search) => new URLSearchParams(search);
var cleanHash = (hash) => decodeURI(hash.startsWith("#") ? hash.substring(1) : hash);
var routingHooks = [];
var counter = 0;
var registerRoutingHook = (callback) => {
  const id = counter++;
  routingHooks.push({ id, callback });
  return id;
};
var removeRoutingHook = function(id) {
  const hookIndex = routingHooks.findIndex((hook) => hook.id === id);
  if (hookIndex)
    console.warn("hook doesnt exist");
  routingHooks.splice(hookIndex, 1);
};
var setRoutingState = async (routingTarget) => {
  const callbackResultPromises = routingHooks.map(
    (hook) => Promise.resolve(hook.callback(routingStateSignal.value, routingTarget))
  );
  if ((await Promise.all(callbackResultPromises)).some((result) => result === false))
    return console.warn("prevented routing");
  _setRoutingState(routingTarget);
  if (!routerConfig.get("USE_VIRTUAL_ROUTING"))
    reflectStateToBrowser(routingTarget.replaceState);
};
var reflectStateToBrowser = (replaceState) => {
  if (routerConfig.get("USE_VIRTUAL_ROUTING"))
    throw new Error("dont reflect state to browser when using virtual routing");
  const newhref = generateHrefFromRoutingState({
    path: routingStateSignal.value.path,
    searchParams: routingStateSignal.value.searchParams,
    hash: routingStateSignal.value.hash
  });
  if (replaceState)
    window.history.replaceState({ path: newhref }, "", newhref);
  else
    window.history.pushState({ path: newhref }, "", newhref);
  window.dispatchEvent(new CustomEvent("location-changed", { node: window }));
};
var _setRoutingState = async (routingTarget) => {
  const next = generateFullRoutingStateFromInput(routingTarget);
  if (next.path === routingStateSignal.value.path && areSearchParamsEqual(next.searchParams, routingStateSignal.value.searchParams) && next.hash === routingStateSignal.value.hash)
    return;
  console.log(
    "%c  routing state :: " + next.path + " // use virtual path: " + (routerConfig.get("USE_VIRTUAL_ROUTING") ? "true" : "false"),
    "color: #bada55"
  );
  routingStateIngest.value = next;
  searchParamsSignal.value = new URLSearchParams(next.searchParams.toString());
};
var routingStateIngest = new Signal(
  routerConfig.get("USE_VIRTUAL_ROUTING") ? {
    path: routerConfig.get("VIRTUAL_PATH") ?? "/",
    searchParams: new URLSearchParams(routerConfig.get("VIRTUAL_SEARCH_PARAMS") ?? ""),
    hash: routerConfig.get("VIRTUAL_HASH") ?? ""
  } : {
    path: window.location.pathname,
    searchParams: new URLSearchParams(window.location.search),
    hash: window.location.hash
  }
);
var routingStateSignal = computed(() => routingStateIngest.value);
var update = () => routingStateIngest.value = {
  path: cleanPath(window.location.pathname),
  searchParams: cleanSearchParams(window.location.search),
  hash: cleanHash(window.location.hash)
};
if (!routerConfig.get("USE_VIRTUAL_ROUTING")) {
  window.addEventListener("hashchange", update);
  window.addEventListener("location-changed", update);
  window.addEventListener("popstate", update);
}
var getSearchParamCount = (searchParams) => {
  if (typeof searchParams.size !== "undefined")
    return searchParams.size;
  return [...searchParams].length;
};
function generateHrefFromRoutingState(routingOptions) {
  const urlQueryParams = getSearchParamCount(routingOptions.searchParams) ? `?${routingOptions.searchParams.toString()}` : "";
  const urlHash = routingOptions.hash ? `#${routingOptions.hash}` : "";
  return `${encodeURI(routingOptions.path)}${urlQueryParams}${urlHash}`;
}
function generateFullRoutingStateFromInput(routingTarget) {
  return {
    path: routingTarget.path && routingTarget.path !== NO_CHANGE_X ? cleanPath(routingTarget.path) : routingStateSignal.value.path,
    searchParams: routingTarget.searchParams ? routingTarget.searchParams === NO_CHANGE_X ? routingStateSignal.value.searchParams : cleanSearchParams(routingTarget.searchParams) : new URLSearchParams(),
    hash: routingTarget.hash ? routingTarget.hash === NO_CHANGE_X ? routingStateSignal.value.hash : cleanHash(routingTarget.hash) : ""
  };
}
function joinPathParts(url1, url2) {
  return `${url1}${url1.endsWith("/") ? "" : "/"}${url2.startsWith("/") ? url2.slice(1) : url2}`;
}
var searchParamsSignal = new Signal(
  routerConfig.get("USE_VIRTUAL_ROUTING") ? new URLSearchParams(routerConfig.get("VIRTUAL_SEARCH_PARAMS") ?? "") : new URLSearchParams(window.location.search)
);
var areSearchParamsEqual = (a, b) => Array.from(a.entries()).every(([key, value]) => b.get(key) === value) && Array.from(b.entries()).every(([key, value]) => a.get(key) === value);
searchParamsSignal.subscribe((searchParams) => {
  if (areSearchParamsEqual(searchParams, routingStateSignal.value.searchParams))
    return;
  setRoutingState({ path: NO_CHANGE_X, searchParams: new URLSearchParams(searchParams.toString()), hash: NO_CHANGE_X });
});
export {
  NO_CHANGE,
  areSearchParamsEqual,
  generateFullRoutingStateFromInput,
  generateHrefFromRoutingState,
  joinPathParts,
  registerRoutingHook,
  removeRoutingHook,
  routingStateSignal,
  searchParamsSignal,
  setRoutingState
};
